import { Cpu, Server } from 'lucide-react';

import { cn } from '@shared/components/ui/cn';

import anthropicSvg from '../assets/model-icons/anthropic.svg?no-inline';
import googleSvg from '../assets/model-icons/google.svg?no-inline';
import openaiSvg from '../assets/model-icons/openai.svg?no-inline';
import xaiSvg from '../assets/model-icons/xai.svg?no-inline';
import type { ModelGroup } from '../service/orchestrator-client';

interface Mark {
  src: string;
  /** Drawn in the text colour, so it reads on both themes; a colour mark is drawn as it is. */
  mono: boolean;
}

/**
 * The provider marks this client bundles (`../assets/model-icons/`, its README names their
 * source), by the icon id `GET /api/v1/models` sends on a group (iq-ai-cio
 * `orchestrator/src/settings/models.py::ICONS`). Imported, never inlined, so the build serves
 * each under `/assets/` with a hashed name: never under `/ai-cio/`, the orchestrator's proxy
 * prefix, where a request goes to the orchestrator.
 */
const MARKS: ReadonlyMap<string, Mark> = new Map<string, Mark>([
  ['anthropic', { src: anthropicSvg, mono: true }],
  ['openai', { src: openaiSvg, mono: true }],
  ['google', { src: googleSvg, mono: false }],
  ['xai', { src: xaiSvg, mono: true }],
]);

/** The icon id of investbanq's own group, drawn as a server: its models run inside Investbanq. */
const INVESTBANQ = 'investbanq';

/** A mark: as it is, or, `mono`, as a mask in the text colour (a colour mark too). */
function Glyph({ mark, mono, className }: { mark: Mark; mono?: boolean; className: string }) {
  if (!(mono ?? mark.mono)) {
    return (
      <img
        src={mark.src}
        alt=""
        aria-hidden="true"
        data-mark={mark.src}
        draggable={false}
        className={cn('shrink-0', className)}
      />
    );
  }
  const image = `url("${mark.src}")`;
  return (
    <span
      aria-hidden="true"
      data-mark={mark.src}
      className={cn('block shrink-0 bg-current', className)}
      style={{
        WebkitMaskImage: image,
        maskImage: image,
        WebkitMaskSize: 'contain',
        maskSize: 'contain',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
      }}
    />
  );
}

/**
 * A provider's logo, 16 px in one colour (the text colour, so both themes): investbanq's group as
 * a server, a bundled provider mark (Google's colour G drawn in the text colour too), else a tile
 * with the group label's first letter (the design's placeholder tile, the Other group's among
 * them). No group (no model shown) draws a chip.
 */
export function ProviderLogo({ group }: { group: ModelGroup | null }) {
  if (!group) return <Cpu aria-hidden className="size-4 shrink-0" />;
  if (group.icon === INVESTBANQ) return <Server aria-hidden className="size-4 shrink-0" />;
  const mark = group.icon ? MARKS.get(group.icon) : undefined;
  if (mark) return <Glyph mark={mark} mono className="size-4" />;
  return (
    <span
      aria-hidden="true"
      className="flex size-4 shrink-0 items-center justify-center rounded-[3px] border text-[10px] font-medium leading-none"
    >
      {Array.from(group.label)[0]}
    </span>
  );
}
